import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  CreateUserInput,
  ListUsersQuery,
  Paginated,
  RoleRef,
  UpdateUserInput,
  UserDetail,
  UserListItem,
} from '@news-cms/shared';
import { ApiError, api } from './api';
import { toast } from '@/components/ui/toaster';

function qs(filter: Partial<ListUsersQuery>): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filter)) {
    if (v === undefined || v === null || v === '') continue;
    params.set(k, String(v));
  }
  return params.toString();
}

export const usersListQueryOptions = (filter: Partial<ListUsersQuery>) =>
  queryOptions({
    queryKey: ['users', 'list', filter] as const,
    queryFn: () => api<Paginated<UserListItem>>(`/api/admin/users?${qs(filter)}`),
  });

export const userDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ['users', 'detail', id] as const,
    queryFn: () => api<{ user: UserDetail }>(`/api/admin/users/${id}`).then((d) => d.user),
  });

export const rolesListQueryOptions = () =>
  queryOptions({
    queryKey: ['roles', 'list'] as const,
    queryFn: () => api<{ items: RoleRef[] }>('/api/admin/users/roles/all').then((d) => d.items),
    staleTime: 5 * 60_000,
  });

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateUserInput) => {
      const data = await api<{ user: UserDetail }>('/api/admin/users', {
        method: 'POST',
        body: JSON.stringify(input),
      });
      return data.user;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users', 'list'] });
      toast.success('สร้างผู้ใช้เรียบร้อย');
    },
    onError: (e) => toast.error(userErrorMessage(e)),
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateUserInput }) => {
      const data = await api<{ user: UserDetail }>(`/api/admin/users/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      });
      return data.user;
    },
    onSuccess: (user) => {
      qc.setQueryData(userDetailQueryOptions(user.id).queryKey, user);
      void qc.invalidateQueries({ queryKey: ['users', 'list'] });
      toast.success('แก้ไขผู้ใช้เรียบร้อย');
    },
    onError: (e) => toast.error(userErrorMessage(e)),
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api<{ ok: true }>(`/api/admin/users/${id}`, { method: 'DELETE' });
      return id;
    },
    onSuccess: (id) => {
      qc.removeQueries({ queryKey: ['users', 'detail', id] });
      void qc.invalidateQueries({ queryKey: ['users', 'list'] });
      toast.success('ลบผู้ใช้เรียบร้อย');
    },
    onError: (e) => toast.error(userErrorMessage(e)),
  });
}

/**
 * Clear the failed-login lockout for a user without changing their
 * password. Server-side: resets failedLoginCount + lockedUntil. Used
 * when a user fat-fingered their way into the 5-attempt threshold
 * and an admin trusts them.
 */
export function useUnlockUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const data = await api<{ user: UserDetail }>(`/api/admin/users/${id}/unlock`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      return data.user;
    },
    onSuccess: (user) => {
      qc.setQueryData(userDetailQueryOptions(user.id).queryKey, user);
      void qc.invalidateQueries({ queryKey: ['users', 'list'] });
      toast.success('ปลดล็อกผู้ใช้แล้ว');
    },
    onError: (e) => toast.error(userErrorMessage(e)),
  });
}

export function userErrorMessage(e: unknown): string {
  if (!(e instanceof ApiError)) return 'เกิดข้อผิดพลาด';
  switch (e.code) {
    case 'EMAIL_IN_USE':
      return 'อีเมลนี้ถูกใช้แล้ว';
    case 'USERNAME_IN_USE':
      return 'username นี้ถูกใช้แล้ว';
    case 'ROLE_NOT_FOUND':
      return 'ไม่พบ role ที่เลือก';
    case 'USER_NOT_FOUND':
      return 'ไม่พบผู้ใช้';
    case 'CANNOT_DELETE_SELF':
      return 'ไม่สามารถลบบัญชีตัวเองได้';
    case 'FORBIDDEN':
      return 'คุณไม่มีสิทธิ์จัดการผู้ใช้';
    case 'VALIDATION_ERROR':
      return 'ข้อมูลไม่ถูกต้อง';
    default:
      return e.message || 'เกิดข้อผิดพลาด';
  }
}
